// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The sync engine. Owns the pull-based protocol on top of the transport session:
 * exchange manifests, decide what to pull, serve what the peer asks for, and
 * write incoming files to the folder store — all while a poll loop watches for
 * local edits and re-announces them. One peer only; a third device is refused.
 */
import { SyncSession, type Hello } from './session.ts';
import type { CryptoClient } from './cryptoclient.ts';
import type { FolderStore } from './fs.ts';
import type { Direction, FileEntry, Manifest, LogEntry, TransferRow } from './types.ts';
import {
  serializeManifest,
  deserializeManifest,
  computeWanted,
  fulfillable,
  changedLocally,
  totalBytes,
  canReceive,
  canSend,
} from './manifest.ts';
import { logicalMtimeFor, type LedgerEntry } from './ledger.ts';
import { fmtBytes, fmtCount } from './format.ts';

const MAX_FILE = 512 * 1024 * 1024; // 512 MB per-file guard
const DEFAULT_POLL_MS = 3000;

export type EngineStatus = 'waiting' | 'paired' | 'syncing' | 'insync' | 'error';

export interface EngineHooks {
  log: (kind: LogEntry['kind'], text: string) => void;
  status: (s: EngineStatus) => void;
  peer: (hello: Hello | null) => void;
  localSummary: (files: number, bytes: number) => void;
  remoteSummary: (files: number, bytes: number) => void;
  transfer: (row: TransferRow) => void;
  extraPeer: () => void;
}

export interface EngineOptions {
  roomId: string;
  pass: string;
  store: FolderStore;
  crypto: CryptoClient;
  direction: Direction;
  name: string;
  pollMs?: number;
  hooks: EngineHooks;
}

export class SyncEngine {
  private session: SyncSession;
  private ledger = new Map<string, LedgerEntry>();
  private local: Manifest = new Map();
  private remote: Manifest = new Map();
  private peerId: string | null = null;
  private peerHello: Hello | null = null;
  private pollTimer: number | null = null;
  private scanning = false;
  private outQueue: string[] = [];
  private outActive = false;
  private inFlightIn = 0;
  private stopped = false;
  private announcedInSync = false;
  private rebroadcastTimer: number | null = null;
  private readonly pollMs: number;

  private store: FolderStore;
  private crypto: CryptoClient;
  private direction: Direction;
  private myHello: Hello;
  private hooks: EngineHooks;

  constructor(opts: EngineOptions) {
    this.store = opts.store;
    this.crypto = opts.crypto;
    this.direction = opts.direction;
    this.hooks = opts.hooks;
    this.pollMs = opts.pollMs ?? DEFAULT_POLL_MS;
    this.myHello = { name: opts.name, direction: opts.direction, kind: opts.store.kind };
    this.session = new SyncSession(opts.roomId, opts.pass, {
      onPeerJoin: (p) => this.onPeerJoin(p),
      onPeerLeave: (p) => this.onPeerLeave(p),
      onHello: (p, h) => this.onHello(p, h),
      onManifest: (p, e) => this.onManifest(p, e),
      onRequest: (p, paths) => this.onRequest(p, paths),
      onFile: (p, frame) => void this.onFile(p, frame),
      onFileProgress: (p, prog, path) => this.onFileProgress(p, prog, path),
      onError: (m) => {
        this.hooks.log('bad', m);
        this.hooks.status('error');
      },
    });
  }

  async start(): Promise<void> {
    await this.crypto.whenReady();
    await this.rescan(true);
    const ok = this.session.join();
    if (!ok) {
      this.hooks.status('error');
      return;
    }
    this.hooks.status('waiting');
    this.hooks.log('info', 'Waiting for the other device to open the same link…');
    this.pollTimer = window.setInterval(() => void this.rescan(false), this.pollMs);
  }

  stop(): void {
    this.stopped = true;
    if (this.pollTimer !== null) clearInterval(this.pollTimer);
    if (this.rebroadcastTimer !== null) clearTimeout(this.rebroadcastTimer);
    this.session.leave();
  }

  // ── peer lifecycle ────────────────────────────────────────────────────────
  private onPeerJoin(peerId: string): void {
    if (this.peerId && this.peerId !== peerId) {
      this.hooks.extraPeer();
      this.hooks.log('warn', 'Another device tried to join. Syncwell pairs two devices only — ignoring it.');
      return;
    }
    this.peerId = peerId;
    this.announcedInSync = false;
    this.hooks.status('paired');
    this.hooks.log('good', 'Device connected — verify the safety words match on both screens.');
    this.session.sendHello(this.myHello, peerId);
    this.session.sendManifest(serializeManifest(this.local), peerId);
  }

  private onPeerLeave(peerId: string): void {
    if (peerId !== this.peerId) return;
    this.peerId = null;
    this.peerHello = null;
    this.remote = new Map();
    this.outQueue = [];
    this.hooks.peer(null);
    this.hooks.remoteSummary(0, 0);
    this.hooks.status('waiting');
    this.hooks.log('warn', 'Device disconnected.');
  }

  private onHello(peerId: string, hello: Hello): void {
    if (peerId !== this.peerId) return;
    this.peerHello = hello;
    this.hooks.peer(hello);
    const iRecv = canReceive(this.direction);
    const iSend = canSend(this.direction);
    const theyRecv = canReceive(hello.direction);
    const theySend = canSend(hello.direction);
    if (!(iRecv && theySend) && !(iSend && theyRecv)) {
      this.hooks.log('warn', 'Both devices are set to the same one-way direction — nothing will transfer. Change one to two-way.');
    }
  }

  // ── manifest exchange ─────────────────────────────────────────────────────
  private onManifest(peerId: string, entries: FileEntry[]): void {
    if (peerId !== this.peerId) return;
    this.remote = deserializeManifest(entries);
    this.hooks.remoteSummary(this.remote.size, totalBytes(this.remote));
    const { request, conflicts } = computeWanted(this.local, this.remote, this.direction);
    for (const c of conflicts) {
      this.hooks.log('warn', `Conflict on “${c}” — changed on both sides at the same time; left untouched.`);
    }
    if (request.length > 0) {
      this.announcedInSync = false;
      this.hooks.status('syncing');
      this.hooks.log('info', `Requesting ${fmtCount(request.length)} from the other device…`);
      this.session.sendRequest(request, peerId);
    } else {
      this.maybeInSync();
    }
  }

  private onRequest(peerId: string, paths: string[]): void {
    if (peerId !== this.peerId) return;
    const serve = fulfillable(this.local, paths, this.direction);
    if (serve.length === 0) return;
    for (const p of serve) if (!this.outQueue.includes(p)) this.outQueue.push(p);
    this.announcedInSync = false;
    this.hooks.status('syncing');
    void this.pumpOut();
  }

  // ── serving files out ─────────────────────────────────────────────────────
  private async pumpOut(): Promise<void> {
    if (this.outActive) return;
    this.outActive = true;
    try {
      while (this.outQueue.length > 0 && this.peerId && !this.stopped) {
        const path = this.outQueue.shift()!;
        const entry = this.local.get(path);
        const read = await this.store.read(path);
        if (!read) {
          this.hooks.log('warn', `Skipped “${path}” — no longer in the folder.`);
          continue;
        }
        const total = read.bytes.length;
        if (total > MAX_FILE) {
          this.hooks.log('warn', `Skipped “${path}” — larger than the ${fmtBytes(MAX_FILE)} per-file limit.`);
          continue;
        }
        const mtime = entry ? entry.mtime : read.mtime;
        this.hooks.transfer({ path, dir: 'out', pct: 0, label: fmtBytes(total), state: 'active' });
        try {
          const frame = await this.crypto.encrypt(path, mtime, total, read.bytes.buffer as ArrayBuffer);
          await this.session.sendFile(this.peerId!, frame, path, (p) =>
            this.hooks.transfer({
              path,
              dir: 'out',
              pct: Math.round(p * 100),
              label: `${fmtBytes(Math.round(p * total))} / ${fmtBytes(total)}`,
              state: 'active',
            }),
          );
          this.hooks.transfer({ path, dir: 'out', pct: 100, label: fmtBytes(total), state: 'done' });
          this.hooks.log('xfer', `Sent “${path}” (${fmtBytes(total)})`);
        } catch (e) {
          this.hooks.transfer({ path, dir: 'out', pct: 0, label: 'failed', state: 'error' });
          this.hooks.log('bad', `Could not send “${path}”: ${(e as Error).message}`);
        }
      }
    } finally {
      this.outActive = false;
      this.maybeInSync();
    }
  }

  // ── receiving files in ────────────────────────────────────────────────────
  private onFileProgress(peerId: string, progress: number, path: string): void {
    if (peerId !== this.peerId || !path) return;
    if (progress >= 1) return; // 'done' row is written once the frame decrypts + writes
    this.hooks.transfer({ path, dir: 'in', pct: Math.round(progress * 100), label: `${Math.round(progress * 100)}%`, state: 'active' });
  }

  private async onFile(peerId: string, frame: Uint8Array): Promise<void> {
    if (peerId !== this.peerId) return;
    this.inFlightIn++;
    // Copy to an exact ArrayBuffer for the worker (trystero may hand us a view).
    const ab = frame.slice().buffer;
    try {
      const { header, bytes } = await this.crypto.decrypt(ab);
      if (!canReceive(this.direction)) {
        this.hooks.log('warn', `Ignored an incoming file — this device is set to send-only.`);
        return;
      }
      await this.store.write(header.path, bytes, header.mtime);
      this.ledger.set(header.path, { version: header.mtime, size: header.size, writtenAt: Date.now() });
      this.local.set(header.path, { path: header.path, size: header.size, mtime: header.mtime });
      this.hooks.transfer({ path: header.path, dir: 'in', pct: 100, label: fmtBytes(header.size), state: 'done' });
      this.hooks.log('xfer', `Received “${header.path}” (${fmtBytes(header.size)})`);
      this.hooks.localSummary(this.local.size, totalBytes(this.local));
      this.scheduleRebroadcast();
    } catch (e) {
      this.hooks.log('bad', `A received file failed to decrypt or save: ${(e as Error).message}`);
      this.hooks.transfer({ path: 'incoming file', dir: 'in', pct: 0, label: 'failed', state: 'error' });
    } finally {
      this.inFlightIn--;
      this.maybeInSync();
    }
  }

  /** After an incoming batch settles, confirm our new manifest to the peer. */
  private scheduleRebroadcast(): void {
    if (this.rebroadcastTimer !== null) clearTimeout(this.rebroadcastTimer);
    this.rebroadcastTimer = window.setTimeout(() => {
      this.rebroadcastTimer = null;
      if (this.peerId) this.session.sendManifest(serializeManifest(this.local), this.peerId);
    }, 400);
  }

  // ── local folder polling ──────────────────────────────────────────────────
  private async rescan(initial: boolean): Promise<boolean> {
    if (this.scanning || this.stopped) return false;
    this.scanning = true;
    try {
      const disk = await this.store.scan();
      const logical: Manifest = new Map();
      for (const [path, e] of disk) {
        const led = this.ledger.get(path);
        const v = logicalMtimeFor({ size: e.size, mtime: e.mtime }, led);
        if (v === e.mtime) {
          this.ledger.set(path, { version: e.mtime, size: e.size, writtenAt: e.mtime });
        }
        logical.set(path, { path, size: e.size, mtime: v });
      }
      for (const p of Array.from(this.ledger.keys())) if (!disk.has(p)) this.ledger.delete(p);

      const changed = changedLocally(this.local, logical);
      this.local = logical;
      this.hooks.localSummary(logical.size, totalBytes(logical));
      if (!initial && changed && this.peerId) {
        this.announcedInSync = false;
        this.session.sendManifest(serializeManifest(this.local), this.peerId);
        this.hooks.log('info', 'Local changes detected — re-announced to the other device.');
      }
      return changed;
    } catch (e) {
      this.hooks.log('bad', `Folder scan failed: ${(e as Error).message}`);
      return false;
    } finally {
      this.scanning = false;
    }
  }

  // ── status ────────────────────────────────────────────────────────────────
  private maybeInSync(): void {
    if (!this.peerId) return;
    if (this.outActive || this.outQueue.length > 0 || this.inFlightIn > 0) return;
    const { request } = computeWanted(this.local, this.remote, this.direction);
    if (request.length > 0) return;
    this.hooks.status('insync');
    if (!this.announcedInSync) {
      this.announcedInSync = true;
      this.hooks.log('good', 'Folders are in sync.');
    }
  }

  peerHelloInfo(): Hello | null {
    return this.peerHello;
  }
}
